import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { getLoginUsername } from "./PermissionsReducer";
import { createAuditLog } from "../utils/commonServices";

const initialState = {
	equipment: [],
	loading: false,
	error: null,
	total: 0,
};

const FETCH_EQUIPMENT_REQUEST = "FETCH_EQUIPMENT_REQUEST";
const FETCH_EQUIPMENT_SUCCESS = "FETCH_EQUIPMENT_SUCCESS";
const FETCH_EQUIPMENT_FAILURE = "FETCH_EQUIPMENT_FAILURE";
const CREATE_EQUIPMENT_REQUEST = "CREATE_EQUIPMENT_REQUEST";
const CREATE_EQUIPMENT_SUCCESS = "CREATE_EQUIPMENT_SUCCESS";
const CREATE_EQUIPMENT_FAILURE = "CREATE_EQUIPMENT_FAILURE";
const UPDATE_EQUIPMENT_REQUEST = "UPDATE_EQUIPMENT_REQUEST";
const UPDATE_EQUIPMENT_SUCCESS = "UPDATE_EQUIPMENT_SUCCESS";
const UPDATE_EQUIPMENT_FAILURE = "UPDATE_EQUIPMENT_FAILURE";
const DELETE_EQUIPMENT_REQUEST = "DELETE_EQUIPMENT_REQUEST";
const DELETE_EQUIPMENT_SUCCESS = "DELETE_EQUIPMENT_SUCCESS";
const DELETE_EQUIPMENT_FAILURE = "DELETE_EQUIPMENT_FAILURE";
const CHECK_IN_OUT_REQUEST = "CHECK_IN_OUT_EQUIPMENT_REQUEST";
const CHECK_IN_OUT_SUCCESS = "CHECK_IN_OUT_EQUIPMENT_SUCCESS";
const CHECK_IN_OUT_FAILURE = "CHECK_IN_OUT_EQUIPMENT_FAILURE";
const FETCH_EQUIPMENT_BY_ID_REQUEST = "FETCH_EQUIPMENT_BY_ID_REQUEST";
const FETCH_EQUIPMENT_BY_ID_SUCCESS = "FETCH_EQUIPMENT_BY_ID_SUCCESS";
const FETCH_EQUIPMENT_BY_ID_FAILURE = "FETCH_EQUIPMENT_BY_ID_FAILURE";

// Fields to track for audit logging by table name
const AUDIT_FIELDS_MAP = {
	Equipment: [
		"propertyNumber",
		"serialNumber",
		"assetTag",
		"brand",
		"model",
		"description",
		"type",
		"status",
		"condition",
		"categoryCode",
		"division",
		"style",
		"caliber",
		"sights",
		"location",
		"purchasedFrom",
		"perPersonnelId",
		"vehicleId",
		"fundingSourceId",
		"assignedDate",
		"lastInspectionDate",
		"purchaseDate",
		"purchasePrice",
		"notes",
	],
	EquipmentMaintenance: [
		"equipmentId",
		"date",
		"type",
		"description",
		"performedBy",
		"cost",
		"notes",
		"nextDueDate",
	],
};

// Single Audit Log Helper for Equipment Module
const logEquipmentAudit = async ({
	tableName = "Equipment",
	action,
	recordId,
	oldData = null,
	newData = null,
	agencyId,
	additionalInfo = null,
}) => {
	try {
		const auditFields =
			AUDIT_FIELDS_MAP[tableName] || AUDIT_FIELDS_MAP.Equipment;

		let oldValues = null;
		let newValues = null;

		if (action === "CREATE") {
			// For CREATE, log all new values
			newValues = {};
			auditFields.forEach((field) => {
				if (
					newData?.[field] !== undefined &&
					newData?.[field] !== null
				) {
					newValues[field] = newData[field];
				}
			});
			// Add additional info if provided
			if (additionalInfo) {
				newValues = { ...newValues, ...additionalInfo };
			}
		} else if (action === "DELETE") {
			// For DELETE, log key old values
			oldValues = {};
			auditFields.forEach((field) => {
				if (
					oldData?.[field] !== undefined &&
					oldData?.[field] !== null
				) {
					oldValues[field] = oldData[field];
				}
			});
		} else if (action === "UPDATE") {
			// For UPDATE, log only changed fields
			oldValues = {};
			newValues = {};
			auditFields.forEach((field) => {
				const oldVal = oldData?.[field];
				const newVal = newData?.[field];
				if (oldVal !== newVal) {
					oldValues[field] = oldVal ?? null;
					newValues[field] = newVal ?? null;
				}
			});

			// Add additional info if provided
			if (additionalInfo) {
				newValues = { ...newValues, ...additionalInfo };
			}

			// Don't log if no changes
			if (
				Object.keys(oldValues).length === 0 &&
				Object.keys(newValues).length === 0
			) {
				return;
			}
		}

		await createAuditLog({
			tableName,
			recordId,
			action,
			changedBy: getLoginUsername() || "Unknown",
			changedAt: new Date().toISOString(),
			oldValues: oldValues ? JSON.stringify(oldValues) : null,
			newValues: newValues ? JSON.stringify(newValues) : null,
			agencyId,
		});
	} catch (error) {
		console.error(`Failed to create audit log for ${tableName}:`, error);
		// Don't throw - audit failure shouldn't break main operation
	}
};

export const getEquipmentById =
	(equipmentId, userAgency) => async (dispatch) => {
		const service = getService(serviceNames.perEquipment);

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_EQUIPMENT_BY_ID_REQUEST,
				success: FETCH_EQUIPMENT_BY_ID_SUCCESS,
				failure: FETCH_EQUIPMENT_BY_ID_FAILURE,
			},
			apiCall: async () => {
				// Get the equipment record
				const equipment = await service.get(equipmentId);

				// Fetch all related data in parallel
				const [
					assignmentHistory,
					serviceRequirements,
					maintenanceRecords,
				] = await Promise.all([
					fetchAssignmentHistory(equipmentId),
					fetchEquipmentServiceRequirements(equipmentId),
					fetchEquipmentMaintenance(equipmentId),
				]);

				return {
					...equipment,
					assignmentHistory,
					serviceRequirements,
					maintenanceRecords,
				};
			},
			errorMessagePrefix: "Failed to fetch equipment",
		});
	};

// SERVICE REQUIREMENTS HELPERS

// Fetch service requirement IDs for an equipment
const fetchEquipmentServiceRequirements = async (equipmentId) => {
	try {
		const service = getService(
			serviceNames.perEquipmentServiceRequirementLinks,
		);
		const response = await service.find({
			query: {
				equipmentId: equipmentId,
				isDeleted: false,
				$limit: -1,
			},
		});

		const data = response?.data || response || [];
		return data.map((sr) => sr.serviceRequirementId);
	} catch (error) {
		console.error("Error fetching equipment service requirements:", error);
		return [];
	}
};

// Save service requirements (same pattern as saveVehicleServiceRequirements)
const saveEquipmentServiceRequirements = async (
	equipmentId,
	serviceRequirementIds,
	userAgency,
) => {
	try {
		const service = getService(
			serviceNames.perEquipmentServiceRequirementLinks,
		);

		// Get existing service requirements
		const existingRequirements = await service.find({
			query: {
				equipmentId: equipmentId,
				isDeleted: false,
				$limit: -1,
			},
		});

		const existingData =
			existingRequirements?.data || existingRequirements || [];
		const existingIds = existingData.map((req) => req.serviceRequirementId);
		const newIds = serviceRequirementIds || [];

		// Find requirements to delete (exist in DB but not in new list)
		const toDelete = existingData.filter(
			(req) => !newIds.includes(req.serviceRequirementId),
		);

		// Find requirements to create (in new list but not in DB)
		const toCreate = newIds.filter((id) => !existingIds.includes(id));

		// Delete removed ones (soft delete)
		for (const req of toDelete) {
			await service.patch(req.id, {
				isDeleted: true,
			});
		}

		// Create new ones
		for (const serviceRequirementId of toCreate) {
			await service.create({
				equipmentId: equipmentId,
				serviceRequirementId,
				agencyId: userAgency,
				isDeleted: false,
			});
		}

		return true;
	} catch (error) {
		console.error("Error saving equipment service requirements:", error);
		throw error;
	}
};

export const createMaintenanceRecord =
	(maintenanceData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perEquipmentMaintenance);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_EQUIPMENT_REQUEST,
				success: UPDATE_EQUIPMENT_SUCCESS,
				failure: UPDATE_EQUIPMENT_FAILURE,
			},
			apiCall: async () => {
				const maintenanceRecord = await service.create({
					...maintenanceData,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});

				// Create Audit Log Entry for Maintenance
				await logEquipmentAudit({
					tableName: "EquipmentMaintenance",
					action: "CREATE",
					recordId: maintenanceRecord.id,
					newData: maintenanceData,
					agencyId: userAgency,
				});

				// Return updated equipment with refreshed maintenance
				const eqService = getService(serviceNames.perEquipment);
				const equipment = await eqService.get(
					maintenanceData.equipmentId,
				);
				const [
					assignmentHistory,
					serviceRequirements,
					maintenanceRecords,
				] = await Promise.all([
					fetchAssignmentHistory(maintenanceData.equipmentId),
					fetchEquipmentServiceRequirements(
						maintenanceData.equipmentId,
					),
					fetchEquipmentMaintenance(maintenanceData.equipmentId),
				]);

				return {
					...equipment,
					assignmentHistory,
					serviceRequirements,
					maintenanceRecords,
				};
			},
			successMessage: "Maintenance record created successfully",
			errorMessagePrefix: "Failed to create maintenance record",
		});
	};

export const updateMaintenanceRecord =
	(id, maintenanceData, userAgency, userId, oldData = {}) =>
	async (dispatch) => {
		const service = getService(serviceNames.perEquipmentMaintenance);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_EQUIPMENT_REQUEST,
				success: UPDATE_EQUIPMENT_SUCCESS,
				failure: UPDATE_EQUIPMENT_FAILURE,
			},
			apiCall: async () => {
				await service.patch(id, {
					...maintenanceData,
					updatedBy: userId,
				});

				// Create Audit Log Entry for Maintenance
				await logEquipmentAudit({
					tableName: "EquipmentMaintenance",
					action: "UPDATE",
					recordId: id,
					oldData,
					newData: maintenanceData,
					agencyId: userAgency,
				});

				// Return updated equipment
				const eqService = getService(serviceNames.perEquipment);
				const equipment = await eqService.get(
					maintenanceData.equipmentId,
				);
				const [
					assignmentHistory,
					serviceRequirements,
					maintenanceRecords,
				] = await Promise.all([
					fetchAssignmentHistory(maintenanceData.equipmentId),
					fetchEquipmentServiceRequirements(
						maintenanceData.equipmentId,
					),
					fetchEquipmentMaintenance(maintenanceData.equipmentId),
				]);

				return {
					...equipment,
					assignmentHistory,
					serviceRequirements,
					maintenanceRecords,
				};
			},
			successMessage: "Maintenance record updated successfully",
			errorMessagePrefix: "Failed to update maintenance record",
		});
	};

// MAINTENANCE HELPERS

const fetchEquipmentMaintenance = async (equipmentId) => {
	try {
		const service = getService(serviceNames.perEquipmentMaintenance);
		const response = await service.find({
			query: {
				equipmentId: equipmentId,
				isDeleted: false,
				$limit: -1,
				$sort: { date: -1 },
			},
		});
		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching equipment maintenance:", error);
		return [];
	}
};

// ASSIGNMENT HISTORY HELPERS

// Fetch assignment history for equipment
const fetchAssignmentHistory = async (equipmentId) => {
	try {
		const service = getService(serviceNames.perEquipmentAssignmentHistory);
		const response = await service.find({
			query: {
				equipmentId: equipmentId,
				isDeleted: false,
				$limit: -1,
				$sort: { assignedDate: -1 },
			},
		});

		return response?.data || response || [];
	} catch (error) {
		console.error("Error fetching assignment history:", error);
		return [];
	}
};

// Create assignment history record
const createAssignmentHistoryRecord = async (data, userAgency, userId) => {
	try {
		const service = getService(serviceNames.perEquipmentAssignmentHistory);
		await service.create({
			equipmentId: data.equipmentId,
			perPersonnelId: data.perPersonnelId || null,
			vehicleId: data.vehicleId || null,
			location: data.location || null,
			assignedDate: data.assignedDate || new Date().toISOString(),
			unassignedDate: null,
			assignedBy: userId,
			notes: data.notes || null,
			agencyId: userAgency,
			createdBy: userId,
			isDeleted: false,
		});
	} catch (error) {
		console.error("Error creating assignment history:", error);
	}
};

// Close previous assignment
const closePreviousAssignment = async (equipmentId, userId) => {
	try {
		const service = getService(serviceNames.perEquipmentAssignmentHistory);
		const response = await service.find({
			query: {
				equipmentId: equipmentId,
				unassignedDate: null,
				isDeleted: false,
				$limit: 1,
				$sort: { assignedDate: -1 },
			},
		});

		const currentAssignment = response?.data?.[0] || response?.[0];
		if (currentAssignment) {
			await service.patch(currentAssignment.id, {
				unassignedDate: new Date().toISOString(),
				updatedBy: userId,
			});
		}
	} catch (error) {
		console.error("Error closing previous assignment:", error);
	}
};

// THUNKS

export const getEquipment =
	(
		page = 0,
		pageSize = 20,
		sortField = "id",
		sortDirection = "desc",
		search = "",
		filters = {},
		userAgency,
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perEquipment);
		const query = {
			$limit: pageSize,
			$skip: page * pageSize,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};

		// Text search
		if (search) {
			query.$or = [
				{ propertyNumber: { $like: `%${search}%` } },
				{ serialNumber: { $like: `%${search}%` } },
				{ assetTag: { $like: `%${search}%` } },
				{ brand: { $like: `%${search}%` } },
				{ model: { $like: `%${search}%` } },
				{ description: { $like: `%${search}%` } },
			];
		}

		// Helper function
		const getQueryValue = (input) => {
			if (Array.isArray(input)) {
				const values = input.map((i) => i.value).filter(Boolean);
				return values.length > 0 ? { $in: values } : undefined;
			} else if (input?.value) {
				return input.value;
			}
			return undefined;
		};

		// Multi-select filter fields
		const multiSelectFields = [
			"type",
			"status",
			"condition",
			"categoryCode",
			"division",
			"brand",
			"style",
			"caliber",
			"sights",
			"location",
			"purchasedFrom",
		];

		multiSelectFields.forEach((field) => {
			const value = getQueryValue(filters[field]);
			if (value !== undefined) {
				query[field] = value;
			}
		});

		// Employee filter
		if (filters.perPersonnelId) {
			const value = getQueryValue(filters.perPersonnelId);
			if (value !== undefined) {
				query.perPersonnelId = value;
			}
		}

		// Vehicle filter
		if (filters.vehicleId) {
			const value = getQueryValue(filters.vehicleId);
			if (value !== undefined) {
				query.vehicleId = value;
			}
		}

		// Funding source filter
		if (filters.fundingSourceId) {
			const value = getQueryValue(filters.fundingSourceId);
			if (value !== undefined) {
				query.fundingSourceId = value;
			}
		}

		// Assignment filter (All / Assigned Out / Unassigned)
		if (filters.assignmentType && filters.assignmentType !== "all") {
			query.assignmentType = filters.assignmentType;
		}

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_EQUIPMENT_REQUEST,
				success: FETCH_EQUIPMENT_SUCCESS,
				failure: FETCH_EQUIPMENT_FAILURE,
			},
			apiCall: async () => {
				const response = await service.find({ query });
				const equipmentData = response?.data || response || [];

				const equipmentWithDetails = await Promise.all(
					equipmentData.map(async (eq) => {
						try {
							const [
								assignmentHistory,
								serviceRequirements,
								maintenanceRecords,
							] = await Promise.all([
								fetchAssignmentHistory(eq.id),
								fetchEquipmentServiceRequirements(eq.id),
								fetchEquipmentMaintenance(eq.id),
							]);

							return {
								...eq,
								assignmentHistory,
								serviceRequirements,
								maintenanceRecords,
							};
						} catch (error) {
							console.error(
								`Error fetching details for equipment ${eq.id}:`,
								error,
							);
							return {
								...eq,
								assignmentHistory: [],
								serviceRequirements: [],
								maintenanceRecords: [],
							};
						}
					}),
				);

				return {
					equipment: equipmentWithDetails,
					total: response.total || equipmentWithDetails.length,
				};
			},
			transformResult: (res) => ({
				equipment: res?.equipment,
				total: res?.total,
			}),
			errorMessagePrefix: "Failed to fetch equipment",
		});
	};

export const createEquipment =
	(equipmentData, serviceRequirementIds = [], userAgency, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perEquipment);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_EQUIPMENT_REQUEST,
				success: CREATE_EQUIPMENT_SUCCESS,
				failure: CREATE_EQUIPMENT_FAILURE,
			},
			apiCall: async () => {
				const equipment = await service.create({
					...equipmentData,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});

				// Save service requirements after equipment is created
				if (serviceRequirementIds.length > 0 && equipment.id) {
					await saveEquipmentServiceRequirements(
						equipment.id,
						serviceRequirementIds,
						userAgency,
					);
				}

				// Create initial assignment history if assigned
				if (equipmentData.perPersonnelId || equipmentData.vehicleId) {
					await createAssignmentHistoryRecord(
						{
							equipmentId: equipment.id,
							perPersonnelId: equipmentData.perPersonnelId,
							vehicleId: equipmentData.vehicleId,
							location: equipmentData.location,
							assignedDate:
								equipmentData.assignedDate ||
								new Date().toISOString(),
							notes: "Initial assignment",
						},
						userAgency,
						userId,
					);
				}

				// Create Audit Log Entry
				await logEquipmentAudit({
					tableName: "Equipment",
					action: "CREATE",
					recordId: equipment.id,
					newData: equipmentData,
					agencyId: userAgency,
					additionalInfo:
						serviceRequirementIds.length > 0
							? { serviceRequirements: serviceRequirementIds }
							: null,
				});

				return {
					...equipment,
					serviceRequirements: serviceRequirementIds,
				};
			},
			successMessage: "Equipment created successfully",
			errorMessagePrefix: "Failed to create equipment",
		});
	};

export const updateEquipment =
	(id, equipmentData, serviceRequirementIds = [], userAgency, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perEquipment);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_EQUIPMENT_REQUEST,
				success: UPDATE_EQUIPMENT_SUCCESS,
				failure: UPDATE_EQUIPMENT_FAILURE,
			},
			apiCall: async () => {
				// Get current equipment to check if assignment changed
				const current = await service.get(id);

				const equipment = await service.patch(id, {
					...equipmentData,
					updatedBy: userId,
				});

				// Update service requirements
				await saveEquipmentServiceRequirements(
					id,
					serviceRequirementIds,
					userAgency,
				);

				// Check if assignment changed
				const assignmentChanged =
					current.perPersonnelId !== equipmentData.perPersonnelId ||
					current.vehicleId !== equipmentData.vehicleId;

				if (assignmentChanged) {
					// Close previous assignment
					await closePreviousAssignment(id, userId);

					// Create new assignment history if assigned
					if (
						equipmentData.perPersonnelId ||
						equipmentData.vehicleId
					) {
						await createAssignmentHistoryRecord(
							{
								equipmentId: id,
								perPersonnelId: equipmentData.perPersonnelId,
								vehicleId: equipmentData.vehicleId,
								location: equipmentData.location,
								assignedDate:
									equipmentData.assignedDate ||
									new Date().toISOString(),
								notes: equipmentData.notes,
							},
							userAgency,
							userId,
						);
					}
				}

				// Fetch updated assignment history
				const assignmentHistory = await fetchAssignmentHistory(id);

				// Create Audit Log Entry
				await logEquipmentAudit({
					tableName: "Equipment",
					action: "UPDATE",
					recordId: id,
					oldData: current,
					newData: equipmentData,
					agencyId: userAgency,
				});

				return {
					...equipment,
					assignmentHistory,
					serviceRequirements: serviceRequirementIds,
				};
			},
			successMessage: "Equipment updated successfully",
			errorMessagePrefix: "Failed to update equipment",
		});
	};

export const deleteEquipment =
	(id, userId, userAgency, oldData = {}) =>
	async (dispatch) => {
		const service = getService(serviceNames.perEquipment);
		const historyService = getService(
			serviceNames.perEquipmentAssignmentHistory,
		);
		const svcReqService = getService(
			serviceNames.perEquipmentServiceRequirementLinks,
		);

		const patchData = {
			isDeleted: true,
			deletedBy: userId,
			deletedDateTime: new Date().toISOString(),
		};

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: DELETE_EQUIPMENT_REQUEST,
				success: DELETE_EQUIPMENT_SUCCESS,
				failure: DELETE_EQUIPMENT_FAILURE,
			},
			apiCall: async () => {
				// Soft delete equipment
				await service.patch(id, patchData);

				// Soft delete all assignment history
				const history = await historyService.find({
					query: { equipmentId: id, isDeleted: false, $limit: -1 },
				});

				const existingHistory = history?.data || history || [];
				for (const h of existingHistory) {
					await historyService.patch(h.id, {
						isDeleted: true,
						deletedBy: userId,
						deletedDateTime: new Date().toISOString(),
					});
				}

				// Soft delete all service requirement links
				const svcReqs = await svcReqService.find({
					query: {
						equipmentId: id,
						isDeleted: false,
						$limit: -1,
					},
				});

				const existingSvcReqs = svcReqs?.data || svcReqs || [];
				for (const sr of existingSvcReqs) {
					await svcReqService.patch(sr.id, {
						isDeleted: true,
						deletedBy: userId,
						deletedDateTime: new Date().toISOString(),
					});
				}

				// Create Audit Log Entry
				await logEquipmentAudit({
					tableName: "Equipment",
					action: "DELETE",
					recordId: id,
					oldData,
					agencyId: userAgency,
				});

				return id;
			},
			transformResult: () => id,
			successMessage: "Equipment deleted successfully",
			errorMessagePrefix: "Failed to delete equipment",
		});
	};

export const checkInOutEquipment =
	(equipmentId, checkData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perEquipment);

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CHECK_IN_OUT_REQUEST,
				success: CHECK_IN_OUT_SUCCESS,
				failure: CHECK_IN_OUT_FAILURE,
			},
			apiCall: async () => {
				// Close previous assignment
				await closePreviousAssignment(equipmentId, userId);

				// Update equipment
				const equipment = await service.patch(equipmentId, {
					status: checkData.status,
					perPersonnelId: checkData.perPersonnelId || null,
					vehicleId: checkData.vehicleId || null,
					location: checkData.location || null,
					assignedDate: new Date().toISOString(),
					assignedBy: userId,
					updatedBy: userId,
				});

				// Create new assignment history
				if (checkData.perPersonnelId || checkData.vehicleId) {
					await createAssignmentHistoryRecord(
						{
							equipmentId: equipmentId,
							perPersonnelId: checkData.perPersonnelId,
							vehicleId: checkData.vehicleId,
							location: checkData.location,
							notes: checkData.notes,
						},
						userAgency,
						userId,
					);
				}

				// Fetch updated data
				const [assignmentHistory, serviceRequirements] =
					await Promise.all([
						fetchAssignmentHistory(equipmentId),
						fetchEquipmentServiceRequirements(equipmentId),
					]);

				return {
					...equipment,
					assignmentHistory,
					serviceRequirements,
				};
			},
			successMessage: "Equipment checked in/out successfully",
			errorMessagePrefix: "Failed to check in/out equipment",
		});
	};

// Bulk audit action
export const auditEquipment =
	(auditData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perEquipment);

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_EQUIPMENT_REQUEST,
				success: UPDATE_EQUIPMENT_SUCCESS,
				failure: UPDATE_EQUIPMENT_FAILURE,
			},
			apiCall: async () => {
				const results = [];

				for (const item of auditData.items) {
					const updated = await service.patch(item.equipmentId, {
						lastInspectionDate: item.lastInspectionDate,
						updatedBy: userId,
					});
					results.push(updated);
				}

				return results;
			},
			successMessage: `${auditData.items.length} item(s) audited successfully`,
			errorMessagePrefix: "Failed to audit equipment",
		});
	};

// Bulk assign action
export const bulkAssignEquipment =
	(assignData, userAgency, userId) => async (dispatch) => {
		const service = getService(serviceNames.perEquipment);

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_EQUIPMENT_REQUEST,
				success: UPDATE_EQUIPMENT_SUCCESS,
				failure: UPDATE_EQUIPMENT_FAILURE,
			},
			apiCall: async () => {
				const results = [];

				for (const equipmentId of assignData.equipmentIds) {
					// Close previous assignment
					await closePreviousAssignment(equipmentId, userId);

					// Build update payload
					const updatePayload = {
						updatedBy: userId,
					};

					if (assignData.perPersonnelId !== undefined) {
						updatePayload.perPersonnelId =
							assignData.perPersonnelId;
						if (assignData.perPersonnelId) {
							updatePayload.assignedDate =
								new Date().toISOString();
							updatePayload.assignedBy = userId;
						}
					}

					if (assignData.vehicleId !== undefined) {
						updatePayload.vehicleId = assignData.vehicleId;
					}

					if (assignData.location !== undefined) {
						updatePayload.location = assignData.location;
					}

					if (assignData.division !== undefined) {
						updatePayload.division = assignData.division;
					}

					if (assignData.status !== undefined) {
						updatePayload.status = assignData.status;
					}

					const updated = await service.patch(
						equipmentId,
						updatePayload,
					);

					if (assignData.perPersonnelId || assignData.vehicleId) {
						await createAssignmentHistoryRecord(
							{
								equipmentId: equipmentId,
								perPersonnelId:
									assignData.perPersonnelId || null,
								vehicleId: assignData.vehicleId || null,
								location: assignData.location || null,
								notes: assignData.notes || "Bulk assignment",
							},
							userAgency,
							userId,
						);
					}

					results.push(updated);
				}

				return results;
			},
			successMessage: `${assignData.equipmentIds.length} item(s) assigned successfully`,
			errorMessagePrefix: "Failed to assign equipment",
		});
	};

// REDUCER

export default function equipmentReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_EQUIPMENT_REQUEST:
		case CREATE_EQUIPMENT_REQUEST:
		case UPDATE_EQUIPMENT_REQUEST:
		case DELETE_EQUIPMENT_REQUEST:
		case CHECK_IN_OUT_REQUEST:
			return { ...state, loading: true, error: null };

		case FETCH_EQUIPMENT_SUCCESS:
			return {
				...state,
				loading: false,
				equipment: action.payload.equipment,
				total: action.payload.total,
			};

		case CREATE_EQUIPMENT_SUCCESS:
			return {
				...state,
				loading: false,
				equipment: [action.payload, ...state.equipment],
				total: state.total + 1,
			};

		case UPDATE_EQUIPMENT_SUCCESS:
			// Handle both single update and bulk update
			if (Array.isArray(action.payload)) {
				const updatedMap = new Map(
					action.payload.map((eq) => [eq.id, eq]),
				);
				return {
					...state,
					loading: false,
					equipment: state.equipment.map((eq) =>
						updatedMap.has(eq.id) ? updatedMap.get(eq.id) : eq,
					),
				};
			}
			return {
				...state,
				loading: false,
				equipment: state.equipment.map((eq) =>
					eq.id === action.payload.id ? action.payload : eq,
				),
			};

		case CHECK_IN_OUT_SUCCESS:
			return {
				...state,
				loading: false,
				equipment: state.equipment.map((eq) =>
					eq.id === action.payload.id ? action.payload : eq,
				),
			};

		case DELETE_EQUIPMENT_SUCCESS:
			return {
				...state,
				loading: false,
				equipment: state.equipment.filter(
					(eq) => eq.id !== action.payload,
				),
				total: state.total - 1,
			};

		case FETCH_EQUIPMENT_BY_ID_REQUEST:
			return { ...state, loading: true, error: null };

		case FETCH_EQUIPMENT_BY_ID_SUCCESS:
			// Update the specific equipment in the list with fresh data
			return {
				...state,
				loading: false,
				equipment: state.equipment.map((eq) =>
					eq.id === action.payload.id ? action.payload : eq,
				),
			};

		case FETCH_EQUIPMENT_BY_ID_FAILURE:
			return { ...state, loading: false, error: action.payload };

		case FETCH_EQUIPMENT_FAILURE:
		case CREATE_EQUIPMENT_FAILURE:
		case UPDATE_EQUIPMENT_FAILURE:
		case DELETE_EQUIPMENT_FAILURE:
		case CHECK_IN_OUT_FAILURE:
			return { ...state, loading: false, error: action.payload };

		default:
			return state;
	}
}
